import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { TX, BUNDLE, TINT_LINER_BUNDLE } from "../data";
import { PRODUCTS, IMAGES, LIP_SHADES } from "../mock";

export const BundleRitual = () => {
  const { t, lang, addToCart } = useApp();
  const tx = TX[lang];
  const [shade, setShade] = useState(LIP_SHADES[1]);
  const save = (BUNDLE.original - BUNDLE.price).toFixed(2);

  const addBundle = () => {
    addToCart(
      { id: BUNDLE.id, name: BUNDLE.name, price: BUNDLE.price, image: BUNDLE.image, preorder: false },
      shade,
      1
    );
  };

  return (
    <section className="py-8">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#F3E2D9] via-[#EFD4C8] to-[#E8C4B6] border border-[#EAD3C6]">
          <div className="relative grid md:grid-cols-2 gap-8 items-center p-8 md:p-14">
            <div>
              <div className="flex items-center gap-2 text-[#B4715C]">
                <Sparkles className="h-4 w-4" />
                <span className="text-[11px] tracking-label uppercase">{tx.bundleKicker}</span>
              </div>
              <h2 className="mt-4 font-serif font-light text-4xl md:text-5xl text-[#3A2C28] leading-[1.02]">
                {tx.bundleTitle}
              </h2>
              <p className="mt-4 max-w-md text-[15px] text-[#6E5A52] leading-relaxed">{BUNDLE.desc[lang]}</p>

              <ul className="mt-6 space-y-2.5">
                {BUNDLE.includes.map((id) => {
                  const p = PRODUCTS.find((x) => x.id === id);
                  return (
                    <li key={id} className="flex items-center gap-3 text-sm text-[#5A4740]">
                      <span className="h-6 w-6 rounded-full bg-white/70 flex items-center justify-center text-[#B4715C]">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      {p?.name[lang]}
                    </li>
                  );
                })}
              </ul>

              <div className="mt-7">
                <p className="text-[11px] tracking-label uppercase text-[#8A756C] mb-2.5">{tx.bundleChoose}</p>
                <div className="flex items-center gap-3.5">
                  {LIP_SHADES.map((s) => (
                    <button
                      key={s.code}
                      onClick={() => setShade(s)}
                      aria-label={`Shade ${s.code}`}
                      className={`relative h-9 w-9 rounded-full transition-all ${
                        shade.code === s.code ? "ring-2 ring-offset-2 ring-[#B4715C] scale-110" : "ring-1 ring-white/70 hover:scale-105"
                      }`}
                      style={{ backgroundColor: s.hex, boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.15), inset 0 3px 5px rgba(255,255,255,0.5)" }}
                    />
                  ))}
                  <span className="text-[12px] text-[#B4715C] ml-1">{shade.code} · {shade.name}</span>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-5">
                <div className="flex items-end gap-3">
                  <span className="font-serif text-4xl text-[#3A2C28]">€{BUNDLE.price.toFixed(2)}</span>
                  <span className="text-lg text-[#A08678] line-through mb-1">€{BUNDLE.original.toFixed(2)}</span>
                </div>
                <span className="text-[11px] tracking-label uppercase px-3 py-1.5 rounded-full bg-[#3A2C28] text-[#F4E6DE]">
                  {tx.bundleSave} €{save}
                </span>
              </div>

              <button
                onClick={addBundle}
                className="mt-7 inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[#B4715C] text-white text-[11px] tracking-label uppercase hover:bg-[#9E5F4B]"
              >
                {tx.bundleAdd}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            <div className="relative grid grid-cols-2 gap-4">
              <div className="col-span-2 rounded-2xl overflow-hidden bg-white/60 backdrop-blur-sm border border-white/60">
                <img src={IMAGES.serum} alt="Serum" className="w-full h-56 object-contain p-6" />
              </div>
              <div className="rounded-2xl overflow-hidden bg-white/60 border border-white/60">
                <img src={IMAGES.lipTints} alt="Lip tint" className="w-full h-36 object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden bg-white/60 border border-white/60">
                <img src={IMAGES.lipliner} alt="Lip liner" className="w-full h-36 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const LipDuo = () => {
  const { lang, addToCart } = useApp();
  const tx = TX[lang];
  const [shade, setShade] = useState(LIP_SHADES[1]);
  const B = TINT_LINER_BUNDLE;
  const save = (B.original - B.price).toFixed(2);

  const addDuo = () => {
    addToCart(
      { id: B.id, name: B.name, price: B.price, image: B.image, preorder: false },
      shade,
      1
    );
  };

  return (
    <section className="py-8">
      <div className="max-w-[1320px] mx-auto px-6">
        <div className="relative overflow-hidden rounded-[2rem] bg-white border border-[#EEE0D7] grid md:grid-cols-[0.9fr_1.1fr] items-center">
          <div className="relative h-full min-h-[280px] bg-gradient-to-br from-[#F1E0D8] to-[#E3BBA9] flex items-center justify-center p-8">
            <img src={IMAGES.lipliner} alt="Lip Duo" className="max-h-72 object-contain" style={{ filter: "drop-shadow(0 20px 24px rgba(120,80,60,0.28))" }} />
            <span className="absolute top-5 left-5 text-[10px] tracking-label uppercase px-3 py-1 rounded-full bg-[#3A2C28] text-[#F4E6DE]">{tx.bundleKicker}</span>
          </div>
          <div className="p-8 md:p-12">
            <p className="text-[11px] tracking-label uppercase text-[#B4715C]">{B.subtitle[lang]}</p>
            <h2 className="mt-3 font-serif font-light text-4xl md:text-5xl text-[#3A2C28] leading-[1.02]">{B.name[lang]}</h2>
            <p className="mt-4 max-w-md text-[15px] text-[#6E5A52] leading-relaxed">{B.desc[lang]}</p>

            <div className="mt-6">
              <p className="text-[11px] tracking-label uppercase text-[#8A756C] mb-2.5">{tx.bundleChoose}</p>
              <div className="flex items-center gap-3.5">
                {LIP_SHADES.map((s) => (
                  <button key={s.code} onClick={() => setShade(s)} aria-label={`Shade ${s.code}`}
                    className={`h-9 w-9 rounded-full transition-all ${shade.code === s.code ? "ring-2 ring-offset-2 ring-[#B4715C] scale-110" : "ring-1 ring-[#E8D6CB] hover:scale-105"}`}
                    style={{ backgroundColor: s.hex, boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.15), inset 0 3px 5px rgba(255,255,255,0.5)" }} />
                ))}
                <span className="text-[12px] text-[#B4715C] ml-1">{shade.code} · {shade.name}</span>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center gap-5">
              <div className="flex items-end gap-3">
                <span className="font-serif text-4xl text-[#3A2C28]">€{B.price.toFixed(2)}</span>
                <span className="text-lg text-[#A08678] line-through mb-1">€{B.original.toFixed(2)}</span>
              </div>
              <span className="text-[11px] tracking-label uppercase px-3 py-1.5 rounded-full bg-[#F4E6DE] text-[#8A6A5C]">{tx.bundleSave} €{save}</span>
            </div>

            <button onClick={addDuo} className="mt-7 inline-flex items-center gap-2 h-12 px-8 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]">
              {tx.bundleAdd} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="mt-3 text-[11px] text-[#A08678]">{lang === "nl" ? "Vandaag verzonden vanuit Nederland." : "Ships today from the Netherlands."}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const BrandStory = () => {
  const { lang } = useApp();
  const tx = TX[lang];
  const navigate = useNavigate();
  return (
    <section className="py-24 md:py-32">
      <div className="max-w-[900px] mx-auto px-6 text-center">
        <span className="text-[11px] tracking-label uppercase text-[#A08678]">{tx.brandKicker}</span>
        <h2 className="mt-5 font-serif font-light text-4xl md:text-6xl text-[#3A2C28] leading-[1.05] tracking-[-0.01em]">
          {tx.brandTitle}
        </h2>
        <p className="mt-7 text-[17px] md:text-[19px] leading-[1.8] text-[#6E5A52]">{tx.brandText}</p>
        <button
          onClick={() => navigate("/about")}
          className="mt-9 text-[11px] tracking-label uppercase text-[#B4715C] link-underline hover:text-[#9E5F4B]"
        >
          {tx.learnMore}
        </button>
      </div>
    </section>
  );
};
